import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApiQuery } from '@/shared/hooks/useApi';
import { api } from '@/shared/services/api';
import { ApiResponse } from '@/shared/types/apiTypes';

import { ProductListParams, ProductListResponse } from '../types/productTypes';

const ITEMS_PER_PAGE = 30;

const fetchProductList = async (
  params: ProductListParams,
): Promise<ApiResponse<ProductListResponse>> => {
  const searchParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
  });

  if (params.keyword && params.keyword.trim()) {
    searchParams.append('keyword', params.keyword.trim());
  }

  return api.get<ProductListResponse>(
    `/api/v1/product/list?filter=all&sort=latest&${searchParams.toString()}`,
  );
};

export const useProductList = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = useState(1);
  const [keyword, setKeyword] = useState('');
  const [shouldReset, setShouldReset] = useState(false);

  // URL에서 파라미터 초기화
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get('page');
    const keywordParam = searchParams.get('keyword');

    const page = pageParam ? parseInt(pageParam, 10) : 1;
    const searchKeyword = keywordParam || '';

    setCurrentPage(page);
    setKeyword(searchKeyword);

    // URL에 기본 파라미터가 없으면 추가
    if (!pageParam) {
      searchParams.set('page', '1');
      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    }
  }, [location, navigate]);

  // 상품 목록 조회
  const {
    data: productListData,
    isLoading,
    error,
    refetch,
  } = useApiQuery(
    ['productList', currentPage, keyword],
    () =>
      fetchProductList({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        keyword,
      }),
    {
      enabled: true,
    },
  );

  // URL 업데이트 함수
  const updateURL = useCallback(
    (page: number, searchKeyword: string) => {
      const searchParams = new URLSearchParams();
      searchParams.set('page', page.toString());

      if (searchKeyword && searchKeyword.trim()) {
        searchParams.set('keyword', searchKeyword.trim());
      }

      navigate(`${location.pathname}?${searchParams.toString()}`, {
        replace: true,
      });
    },
    [location.pathname, navigate],
  );

  // 페이지 변경
  const handlePageChange = useCallback(
    (page: number) => {
      setCurrentPage(page);
      updateURL(page, keyword);
    },
    [keyword, updateURL],
  );

  // 검색 처리
  const handleSearch = useCallback(
    (searchTerm: string) => {
      setKeyword(searchTerm);
      setCurrentPage(1);
      updateURL(1, searchTerm);
    },
    [updateURL],
  );

  // 초기화 처리
  const handleReset = useCallback(() => {
    setKeyword('');
    setCurrentPage(1);
    setShouldReset(true);
    updateURL(1, '');
  }, [updateURL]);

  // shouldReset 완료 처리
  const handleResetComplete = useCallback(() => {
    setShouldReset(false);
  }, []);

  // 새로고침 처리
  const handleRefresh = useCallback(() => {
    refetch();
  }, [refetch]);

  // 계산된 값들
  const products = productListData?.data?.productOptionList || [];
  const totalCount = productListData?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
  const hasError = !!error;
  const isEmpty = !isLoading && !hasError && products.length === 0;

  return {
    // 상태
    products,
    currentPage,
    totalPages,
    keyword,
    isLoading,
    hasError,
    isEmpty,
    shouldReset,
    totalCount,

    // 액션
    handlePageChange,
    handleSearch,
    handleReset,
    handleResetComplete,
    handleRefresh,

    // 에러 정보
    error,
  };
};
